import { z } from 'zod';

export const OrderRefundTypeSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string().nullish(),
});
export type OrderRefundType = z.infer<typeof OrderRefundTypeSchema>;

export const OrderItemRefundSchema = z.object({
  id: z.union([z.string(), z.number()]),
  isRefunded: z.boolean(),
  name: z.string(),
  shippingFeeValue: z.number(),
  discountFeeValue: z.number(),
  authFeeValue: z.number(),
  dutyAndTaxFeeValue: z.number(),
  price: z.number(),
});
export type OrderItemRefund = z.infer<typeof OrderItemRefundSchema>;

export const OrderRefundDetailSchema = z.object({
  currency: z.string(),
  currencyChange: z.number(),
  currencyDigitDecimals: z.number(),
  currencySymbol: z.string().nullable(),
  totalOrderValue: z.number(),
  totalRefundedValue: z.number(),
  totalPaidValue: z.number(),
  remainValue: z.number(),
  products: OrderItemRefundSchema.array(),
  types: OrderRefundTypeSchema.array(),
});
export type OrderRefundDetail = z.infer<typeof OrderRefundDetailSchema>;

export const OrderExtraRefundDetailSchema = z.object({
  currency: z.string(),
  currencySymbol: z.string().nullable(),
  dutyAndTaxFee: z.number(),
  hasExtraRefund: z.boolean(),
  shippingFee: z.number(),
  totalOrderValue: z.number(),
  totalRemainValue: z.number(),
  types: OrderRefundTypeSchema.array(),
});
export type OrderExtraRefundDetail = z.infer<typeof OrderExtraRefundDetailSchema>;

export const OrderRefundSchema = z.object({
  itemId: z.string().nullable(),
  createdAt: z.string(),
  type: z.string(),
  amount: z.string(),
  comment: z.string(),
  status: z.string(),
  internalComment: z.string(),
  isApproved: z.boolean(),
  state: z.number(), // STATE_REFUND_TO_DO = 0; STATE_REFUND_BEING_PROCESS = 3; STATE_REFUND_SUCCESS = 1; STATE_REFUND_FAILED = 2; STATE_REFUND_ON_HOLD = 4;
  refundAccount: z.string().nullish(),
  failedReason: z.string().nullish(),
  executedAt: z.string().nullish(),
  brandName: z.string().nullish().optional(),
  productName: z.string().nullish().optional(),
});
export type OrderRefund = z.infer<typeof OrderRefundSchema>;

export const ProofOfRefundSchema = z.object({
  paymentStatus: z.string().nullish().optional(),
  paymentMethod: z.string().nullish().optional(),
  refundList: OrderRefundSchema.array(),
});

export type ProofOfRefund = z.infer<typeof ProofOfRefundSchema>;

export const OrderProductSchema = z.object({
  sellerId: z.string(),
  productId: z.string(),
  price: z.string(),
  authenticationFee: z.string(),
  shippingFee: z.string(),
  dutyFee: z.string(),
  buyerServiceFee: z.string().nullable(),
  discount: z.string(),
});
export type OrderProduct = z.infer<typeof OrderProductSchema>;

export const PaymentInfoSchema = z.object({
  id: z.number().nullish(),
  validationTypeForFraud: z.string().nullish(),
  paymentMethod: z.string().nullish(),
  cardType: z.string().nullish(),
  credits: z.union([z.string(), z.number()]).nullish(),
  notes: z.string().nullish(),
  notesCancellation: z.string().nullish(),
  status: z.string().nullish(),
  statusCode: z.number().nullish(),
  authorizedAt: z.string().nullish(),
  capturedAt: z.string().nullish(),
  cancelledAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  delayedAt: z.string().nullish(),
  toBeCapturedAmountInfo: z.string().nullish(),
  capturedAmountInfo: z.string().nullish(),
});
export type PaymentInfo = z.infer<typeof PaymentInfoSchema>;

export const OrderSchema = z.object({
  orderId: z.union([z.string(), z.number()]),
  invoiceDate: z.string(),
  total: z.string(),
  shippingFee: z.union([z.string(), z.number()]),
  markUpFee: z.union([z.string(), z.number()]),
  dutyFee: z.union([z.string(), z.number()]),
  salesTax: z.union([z.string(), z.number()]),
  refunds: z.union([z.string(), z.number()]),
  refundList: OrderRefundSchema.array(),
  firstName: z.string(),
  lastName: z.string(),
  orderAddress1: z.string(),
  orderAddress2: z.string(),
  orderCity: z.string(),
  orderDistrict: z.string(),
  orderProvince: z.string(),
  orderZipCode: z.string(),
  orderCountry: z.string(),
  orderTelephone: z.string(),
  orderCompany: z.string(),
  paymentId: z.string().nullable(),
  pickUpPointName: z.string(),
  pickUpAddress: z.string(),
  pickUpCity: z.string(),
  pickUpZipCode: z.string(),
  pickUpType: z.string(),
  products: OrderProductSchema.array().optional(),
  buyerId: z.string(),
  invoiceLink: z.string().nullable(),
  invoiceLitigationLink: z.string().nullable(),
  isUpdateDeliveryAddressEnabled: z.boolean(),
  authenticationFee: z.string(),
  totalDiscount: z.string(),
  buyerServiceFee: z.string().nullable().optional(),
  promoCode: z.string(),
  paymentInfo: PaymentInfoSchema.optional(),
});

export type Order = z.infer<typeof OrderSchema>;

export const OrderInvoiceSchema = z.object({
  link: z.string(),
});
export type OrderInvoice = z.infer<typeof OrderInvoiceSchema>;

export const ChargeBackSchema = z.object({
  disputeId: z.string().nullish(),
  orderId: z.string().nullish(),
  disputeDateTimeParis: z.string().nullish(),
  disputeOutcome: z.string().nullish(),
  disputeReason: z.string().nullish(),
  disputeAmountLocal: z.string().nullish(),
  disputeCurrency: z.string().nullish(),
  disputeAmountEur: z.string().nullish(),
  paymentProvider: z.string().nullish(),
});

export type ChargeBack = z.infer<typeof ChargeBackSchema>;
